import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  BookOpen, 
  Video, 
  ExternalLink, 
  Plus, 
  X, 
  Star, 
  CheckCircle2,
  Loader2,
  Lightbulb,
  Users,
  Wrench,
  Target,
  Play
} from "lucide-react";
import { toast } from "sonner";
import { goalsService } from "@/services/goalsService";

interface GrowthGoal {
  id: string;
  title: string;
  description?: string;
  category: string;
  skills_needed?: string[];
}

interface Resource {
  id?: string;
  goal_id?: string;
  title: string;
  description?: string;
  url?: string;
  resource_type: 'article' | 'video' | 'course' | 'book' | 'tool' | 'person' | 'project';
  ai_recommended: boolean;
  user_rating?: number;
  completed?: boolean;
  created_at?: string;
}

interface GoalResourcesModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  goal: GrowthGoal;
}

const RESOURCE_TYPES = [
  { value: 'article', label: 'Article', icon: BookOpen },
  { value: 'video', label: 'Video', icon: Video },
  { value: 'course', label: 'Course', icon: Play },
  { value: 'book', label: 'Book', icon: BookOpen },
  { value: 'tool', label: 'Tool', icon: Wrench },
  { value: 'person', label: 'Person', icon: Users },
  { value: 'project', label: 'Project', icon: Target }
];

export default function GoalResourcesModal({
  isOpen,
  onOpenChange,
  goal,
}: GoalResourcesModalProps) {
  const [resources, setResources] = useState<Resource[]>([]);
  const [aiSuggestions, setAiSuggestions] = useState<Resource[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isAddingResource, setIsAddingResource] = useState(false);
  
  // Add resource form
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [url, setUrl] = useState("");
  const [resourceType, setResourceType] = useState<Resource['resource_type']>('article');

  useEffect(() => {
    if (isOpen && goal?.id) {
      loadResources();
      generateAIRecommendations();
    }
  }, [isOpen, goal?.id]);

  const loadResources = async () => {
    if (!goal?.id) return;
    
    setIsLoading(true);
    try {
      // apiService.get returns unwrapped data directly (response.data.data)
      // Backend returns { resources: [...] }
      const response = await goalsService.getResources(goal.id);
      const resourcesData = response?.resources || response || [];

      if (Array.isArray(resourcesData)) {
        setResources(resourcesData.map((r: any) => ({
          id: r.id,
          goal_id: r.goalId || r.goal_id,
          title: r.title,
          description: r.description,
          url: r.url,
          resource_type: r.resourceType || r.resource_type,
          ai_recommended: r.aiRecommended || r.ai_recommended || false,
          user_rating: r.userRating || r.user_rating,
          completed: r.completed || false,
          created_at: r.createdAt || r.created_at,
        })));
      } else {
        setResources([]);
      }
    } catch (error) {
      console.error('Error loading resources:', error);
      setResources([]);
    } finally {
      setIsLoading(false);
    }
  };

  // ALIGNED WITH web_old: generateAIRecommendations with backend API
  // Backend endpoint: GET /goals/:goalId/resources/recommendations?forceRefresh=false
  // MIGRATED FROM web_old/src/components/growth/GoalResourcesModal.tsx::generateAIRecommendations()
  const generateAIRecommendations = async () => {
    if (!goal?.id) return;
    
    setIsGenerating(true);
    try {
      // ALIGNED WITH web_old: Call backend endpoint (lines 159-162)
      // Backend now returns format matching web_old exactly (no mapping needed)
      const response = await goalsService.getResourceRecommendations(goal.id, true); // ALIGNED: forceRefresh: true (line 161)
      const recommendations = response?.recommendations || [];
      
      // ALIGNED WITH web_old: Backend returns format directly matching Resource interface
      // No mapping needed - backend format matches web_old format exactly
      setAiSuggestions(Array.isArray(recommendations) ? recommendations.slice(0, 5) : []);
    } catch (error) {
      console.error('Error generating AI recommendations:', error);
      // ALIGNED WITH web_old: Fallback recommendations (lines 169-185)
      setAiSuggestions([
        {
          title: "GitHub Learning Resources",
          description: "Explore open source projects and documentation related to your goal.",
          url: "https://github.com/topics",
          resource_type: "tool",
          ai_recommended: true
        },
        {
          title: "Coursera Professional Courses",
          description: "Find structured courses that align with your growth objectives.",
          url: "https://www.coursera.org",
          resource_type: "course",
          ai_recommended: true
        }
      ]);
    } finally {
      setIsGenerating(false);
    }
  };

  const addResource = async (resource: Resource) => {
    if (!goal?.id) return;

    try {
      // apiService.post returns unwrapped data directly (response.data.data)
      const data = await goalsService.addResource(goal.id, {
        title: resource.title,
        description: resource.description,
        url: resource.url,
        resourceType: resource.resource_type,
        aiRecommended: resource.ai_recommended || false,
      });

      if (data && data.id) {
        const newResource = {
          ...resource,
          id: data.id,
          goal_id: goal.id,
          created_at: data.createdAt || data.created_at,
        };
        setResources(prev => [newResource, ...prev]);
        toast.success('Resource added!');
      } else {
        throw new Error('Failed to add resource');
      }
    } catch (error) {
      console.error('Error adding resource:', error);
      toast.error('Failed to add resource');
    }
  };

  const addCustomResource = async () => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }

    setIsAddingResource(true);
    try {
      await addResource({
        title: title.trim(),
        description: description.trim() || undefined,
        url: url.trim() || undefined,
        resource_type: resourceType,
        ai_recommended: false
      });

      // Reset form
      setTitle("");
      setDescription("");
      setUrl("");
      setResourceType('article');
    } catch (error) {
      console.error('Error adding custom resource:', error);
    } finally {
      setIsAddingResource(false);
    }
  };

  const toggleCompleted = async (resourceId: string, completed: boolean) => {
    if (!goal?.id) return;
    
    try {
      if (completed) {
        // Mark as completed via API
        await goalsService.markResourceCompleted(goal.id, resourceId);
        toast.success('Resource marked as completed');
      }
      // Update local state
      setResources(prev => prev.map(r => 
        r.id === resourceId ? { ...r, completed } : r
      ));
    } catch (error) {
      console.error('Error updating resource:', error);
      toast.error('Failed to update resource');
    }
  };

  const getResourceIcon = (type: string) => {
    const resourceType = RESOURCE_TYPES.find(t => t.value === type);
    const Icon = resourceType?.icon || BookOpen;
    return <Icon className="h-4 w-4" />;
  };

  const getTypeColor = (type: string) => {
    const colors = {
      article: 'bg-blue-100 text-blue-700',
      video: 'bg-red-100 text-red-700',
      course: 'bg-purple-100 text-purple-700',
      book: 'bg-green-100 text-green-700',
      tool: 'bg-orange-100 text-orange-700',
      person: 'bg-pink-100 text-pink-700',
      project: 'bg-indigo-100 text-indigo-700'
    };
    return colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-700';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Resources for "{goal?.title}"
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="resources" className="mt-4">
          <TabsList className="mb-4">
            <TabsTrigger value="resources">My Resources</TabsTrigger>
            <TabsTrigger value="suggestions">AI Suggestions</TabsTrigger>
            <TabsTrigger value="add">Add Resource</TabsTrigger>
          </TabsList>

          <TabsContent value="resources">
            <ScrollArea className="h-[400px]">
              <div className="space-y-3">
                {isLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : resources.length === 0 ? (
                  <div className="text-center py-8">
                    <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">No resources added yet</p>
                    <p className="text-sm text-muted-foreground">Check out AI suggestions or add your own!</p>
                  </div>
                ) : (
                  resources.map((resource) => (
                    <Card key={resource.id} className={resource.completed ? 'opacity-60' : ''}>
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              {getResourceIcon(resource.resource_type)}
                              <h4 className="font-medium">{resource.title}</h4>
                              <Badge className={`text-xs ${getTypeColor(resource.resource_type)}`}>
                                {resource.resource_type}
                              </Badge>
                              {resource.ai_recommended && (
                                <Badge variant="secondary" className="text-xs">
                                  <Lightbulb className="h-3 w-3 mr-1" />
                                  AI
                                </Badge>
                              )}
                            </div>
                            {resource.description && (
                              <p className="text-sm text-muted-foreground mb-2">{resource.description}</p>
                            )}
                            {resource.url && (
                              <a
                                href={resource.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                              >
                                Open resource <ExternalLink className="h-3 w-3" />
                              </a>
                            )}
                          </div>
                          <Button
                            variant={resource.completed ? "default" : "outline"}
                            size="sm"
                            onClick={() => toggleCompleted(resource.id!, !resource.completed)}
                          >
                            <CheckCircle2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="suggestions">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  AI-curated resources specifically for your goal
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={generateAIRecommendations}
                  disabled={isGenerating}
                >
                  {isGenerating ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  ) : (
                    <Lightbulb className="h-3 w-3 mr-1" />
                  )}
                  Refresh
                </Button>
              </div>

              <ScrollArea className="h-[350px]">
                <div className="space-y-3">
                  {isGenerating ? (
                    <div className="text-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">Finding the best resources for your goal...</p>
                    </div>
                  ) : aiSuggestions.length === 0 ? (
                    <div className="text-center py-8">
                      <Lightbulb className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground">No AI suggestions available</p>
                    </div>
                  ) : (
                    aiSuggestions.map((suggestion, index) => (
                      <Card key={index} className="border-l-4 border-l-purple-500">
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                {getResourceIcon(suggestion.resource_type)}
                                <h4 className="font-medium">{suggestion.title}</h4>
                                <Badge className={`text-xs ${getTypeColor(suggestion.resource_type)}`}>
                                  {suggestion.resource_type}
                                </Badge>
                                <Badge variant="secondary" className="text-xs">
                                  <Star className="h-3 w-3 mr-1" />
                                  AI Curated
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground mb-2">{suggestion.description}</p>
                              {suggestion.url && (
                                <a
                                  href={suggestion.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                                >
                                  Preview resource <ExternalLink className="h-3 w-3" />
                                </a>
                              )}
                            </div>
                            <Button
                              size="sm"
                              onClick={() => addResource(suggestion)}
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Add
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          </TabsContent>

          <TabsContent value="add">
            <div className="space-y-4">
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="resource-title">Title *</Label>
                  <Input
                    id="resource-title"
                    placeholder="Resource name"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="resource-description">Description</Label>
                  <Textarea
                    id="resource-description"
                    placeholder="Why is this resource helpful for your goal?"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="resource-url">URL (optional)</Label>
                  <Input
                    id="resource-url"
                    placeholder="https://..."
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                  />
                </div>

                <div className="grid gap-2">
                  <Label>Resource Type</Label>
                  <Select value={resourceType} onValueChange={(value: Resource['resource_type']) => setResourceType(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RESOURCE_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <type.icon className="h-4 w-4" />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  onClick={addCustomResource} 
                  disabled={!title.trim() || isAddingResource}
                  className="w-full"
                >
                  {isAddingResource && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Add Resource
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}